import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { authApi } from "../api/endpoints";
import { setUnauthorizedHandler } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | unauthed
  const [expiredBanner, setExpiredBanner] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      setStatus("authed");
    } catch (e) {
      setUser(null);
      setStatus("unauthed");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // On any 401 from a subsequent call: clear session and show banner.
      if (status === "authed") setExpiredBanner(true);
      setUser(null);
      setStatus("unauthed");
    });
    return () => setUnauthorizedHandler(null);
  }, [status]);

  const login = useCallback(async (email, password) => {
    const me = await authApi.login(email, password);
    setUser(me);
    setStatus("authed");
    setExpiredBanner(false);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // swallow — server may have already invalidated
    }
    setUser(null);
    setStatus("unauthed");
  }, []);

  const value = useMemo(
    () => ({
      user, status, expiredBanner, refresh, login, logout,
      clearExpiredBanner: () => setExpiredBanner(false),
    }),
    [user, status, expiredBanner, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth used outside AuthProvider");
  return ctx;
}
